<template>
    <div
        v-if="suggestedReply"
        class="relative w-full rounded-t-lg min-h-11 border-b-0 rounded-b-none bg-violet-50 border border-solid border-violet-100 px-3 py-2 -mb-[8px]"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
    >
        <motion.div
            class="overflow-hidden"
            :animate="{ height: hovered ? 'auto' : '1.2rem' }"
            :transition="{ duration: 0.2, ease: 'easeOut' }"
        >
            <p ref="textElement" class="text-xs text-violet-900 m-0 whitespace-pre-line pl-5 pb-2 pr-[7.5rem]">
                {{ suggestedReply }}
            </p>
        </motion.div>
        <div class="absolute inset-s-3 inset-e-3 inset-be-3.5 flex items-center justify-between gap-2">
            <font-awesome-icon :icon="faSparkles" class="text-violet-500 text-xs shrink-0" />
            <vx-button outline color="violet" size="xs" class="shrink-0" @click.stop="useSuggestedReply">
                Use Suggestion
            </vx-button>
        </div>
        <div
            v-if="overflows"
            class="absolute inset-be-3.5 inset-s-0 inset-e-0 flex justify-center pointer-events-none transition-opacity duration-200 ease-out"
            :class="hovered ? 'opacity-0' : 'opacity-100'"
        >
            <font-awesome-icon :icon="faEllipsis" class="text-violet-500 text-xs translate-y-1/2" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faEllipsis, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { computed, inject, nextTick, ref, watch } from 'vue';
import { motion } from 'motion-v';
import { useIntelligentInbox } from '~/components/message-hub/intelligent-inbox/useIntelligentInbox';
import { shouldShowThreadSuggestion } from '~/components/message-hub/messageHubUtil';
import { useConversation } from '~/composables/useConversation';
import type { Features } from '~/provides/features.types';

const props = defineProps<{
    focusTextarea?: (options?: { cursorAtEnd?: boolean }) => void | Promise<void>;
}>();

const FEATURES = inject('FEATURES') as Features;

const { activeThread, message } = useConversation();

const { fetchSuggestion } = useIntelligentInbox();

const hovered = ref(false);
const textElement = ref<HTMLElement | null>(null);
const overflows = ref(false);
const suggestedReply = ref<string | undefined>('');

const suggestionTarget = computed(() => {
    if (!shouldShowThreadSuggestion(FEATURES, activeThread.value)) {
        return undefined;
    }

    return {
        contactId: activeThread.value.contact_id,
        groupId: activeThread.value.group_id ?? '',
    };
});

watch(suggestedReply, async () => {
    await nextTick();
    const element = textElement.value;
    overflows.value = !!element && element.scrollHeight > 20;
});

watch(
    suggestionTarget,
    async (target, _previousTarget, onCleanup) => {
        hovered.value = false;

        if (!target) {
            suggestedReply.value = '';
            overflows.value = false;
            return;
        }

        suggestedReply.value = undefined;

        let cancelled = false;
        onCleanup(() => {
            cancelled = true;
        });

        const reply = await fetchSuggestion(target.groupId, target.contactId);
        if (!cancelled) {
            suggestedReply.value = reply;
        }
    },
    { immediate: true }
);

const useSuggestedReply = () => {
    if (suggestedReply.value) {
        message.body = suggestedReply.value;
        props.focusTextarea?.({ cursorAtEnd: true });
    }
};
</script>
